import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User, Ticket, Calendar, TrendingUp, Activity, Clock,
  ChevronRight, AlertTriangle, Check, Target, Plus,
  ArrowUpRight, ArrowDownRight, Zap, BarChart3
} from 'lucide-react'
import { adminApi } from '../services/ApiService'
import { useAuth } from '../context/AuthContext'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import './MiDashboard.css'

const STAGES = [
  { value: 'lead', label: 'Lead', color: '#94a3b8' },
  { value: 'contacto', label: 'Contacto', color: '#60a5fa' },
  { value: 'demo', label: 'Demo', color: '#a78bfa' },
  { value: 'propuesta', label: 'Propuesta', color: '#f59e0b' },
  { value: 'negociacion', label: 'Negociación', color: '#fb923c' },
  { value: 'cerrado_ganado', label: 'Ganado', color: '#4ade80' },
  { value: 'cerrado_perdido', label: 'Perdido', color: '#f87171' },
]

function formatCurrency(n) {
  if (!n) return '$0'
  return '$' + Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })
}

function daysInStage(deal) {
  const d = deal.stageChangedAt || deal.lastContact || deal.$createdAt
  if (!d) return null
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000)
}

export default function MiDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [myTickets, setMyTickets] = useState([])
  const [myActivities, setMyActivities] = useState([])
  const [allActivities, setAllActivities] = useState([])
  const [myDeals, setMyDeals] = useState([])
  const [allDeals, setAllDeals] = useState([])
  const [myAudit, setMyAudit] = useState([])
  const [myGoals, setMyGoals] = useState([])

  const email = user?.email || ''
  const todayStr = new Date().toISOString().substring(0, 10)
  const now = new Date()

  useEffect(() => {
    if (!email) return
    setLoading(true)
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    Promise.all([
      adminApi.get('/admin/tickets').then(r => r.tickets || []).catch(() => []),
      adminApi.get('/admin/activities', { month }).then(r => r.activities || r.documents || []).catch(() => []),
      adminApi.get('/admin/pipeline').then(r => r.deals || r.documents || []).catch(() => []),
      adminApi.get('/admin/audit-logs', { user: email }).then(r => r.logs || []).catch(() => []),
      adminApi.get('/admin/goals', { period: month }).then(r => r.goals || r.documents || []).catch(() => []),
    ]).then(([tickets, activities, deals, audit, goals]) => {
      setMyTickets(tickets.filter(t => t.assignedTo === email && t.status !== 'closed' && t.status !== 'resolved'))
      setAllActivities(activities)
      setMyActivities(activities.filter(a => a.assignedTo === email))
      setAllDeals(deals)
      setMyDeals(deals.filter(d => d.owner === email))
      setMyAudit(audit.slice(0, 8))
      setMyGoals(goals)
      setLoading(false)
    })
  }, [email])

  const todayActivities = useMemo(() =>
    myActivities.filter(a => a.date?.substring(0, 10) === todayStr && a.status !== 'completada')
      .sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [myActivities, todayStr])

  const activeDeals = useMemo(() =>
    myDeals.filter(d => !['cerrado_ganado', 'cerrado_perdido'].includes(d.stage)),
    [myDeals])

  const staleDeals = useMemo(() =>
    activeDeals.filter(d => daysInStage(d) > 14).sort((a, b) => (daysInStage(b) || 0) - (daysInStage(a) || 0)),
    [activeDeals])

  const pipelineValue = useMemo(() => activeDeals.reduce((s, d) => s + (d.value || 0), 0), [activeDeals])
  const wonDeals = useMemo(() => myDeals.filter(d => d.stage === 'cerrado_ganado'), [myDeals])
  const totalWon = useMemo(() => wonDeals.reduce((s, d) => s + (d.value || 0), 0), [wonDeals])

  const weekActivities = useMemo(() => {
    const weekAgo = new Date(now)
    weekAgo.setDate(weekAgo.getDate() - 7)
    const weekStr = weekAgo.toISOString().substring(0, 10)
    return myActivities.filter(a => a.status === 'completada' && a.date?.substring(0, 10) >= weekStr).length
  }, [myActivities])

  const overdueActivities = useMemo(() =>
    myActivities.filter(a => a.status === 'pendiente' && a.date?.substring(0, 10) < todayStr),
    [myActivities, todayStr])

  const goalStats = useMemo(() => {
    const total = myGoals.length
    const achieved = myGoals.filter(g => g.current >= g.target && g.target > 0).length
    const avgProgress = total > 0
      ? myGoals.reduce((sum, g) => sum + Math.min(100, g.target > 0 ? (g.current / g.target) * 100 : 0), 0) / total
      : 0
    return { total, achieved, avgProgress: avgProgress.toFixed(0) }
  }, [myGoals])

  const dealsByStageChart = useMemo(() => {
    return STAGES.filter(s => !['cerrado_ganado', 'cerrado_perdido'].includes(s.value)).map(s => ({
      name: s.label,
      count: activeDeals.filter(d => d.stage === s.value).length,
      color: s.color,
    }))
  }, [activeDeals])

  if (loading) return <div className="mid-page"><div className="mid-loading"><div className="mid-spinner" /> Cargando tu dashboard...</div></div>

  return (
    <div className="mid-page">
      {/* Header */}
      <div className="mid-header">
        <div className="mid-header-icon"><User size={28} /></div>
        <div>
          <h1>Mi Dashboard</h1>
          <p className="mid-subtitle">Hola, {user?.name || email} · {now.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
        <div className="mid-header-actions">
          <button className="mid-quick-btn" onClick={() => navigate('/calendar')}><Plus size={14} /> Actividad</button>
          <button className="mid-quick-btn" onClick={() => navigate('/pipeline')}><Plus size={14} /> Deal</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="mid-kpis">
        <div className="mid-kpi" onClick={() => navigate('/tickets')}>
          <Ticket size={20} />
          <span className="mid-kpi-num">{myTickets.length}</span>
          <span className="mid-kpi-label">Tickets abiertos</span>
        </div>
        <div className="mid-kpi" onClick={() => navigate('/calendar')}>
          <Calendar size={20} />
          <span className="mid-kpi-num">{todayActivities.length}</span>
          <span className="mid-kpi-label">Hoy pendientes</span>
        </div>
        <div className="mid-kpi" onClick={() => navigate('/pipeline')}>
          <TrendingUp size={20} />
          <span className="mid-kpi-num">{activeDeals.length}</span>
          <span className="mid-kpi-label">Deals activos</span>
        </div>
        <div className="mid-kpi">
          <BarChart3 size={20} />
          <span className="mid-kpi-num" style={{ color: '#4ade80' }}>{formatCurrency(pipelineValue)}</span>
          <span className="mid-kpi-label">Pipeline</span>
        </div>
        <div className="mid-kpi">
          <ArrowUpRight size={20} style={{ color: '#4ade80' }} />
          <span className="mid-kpi-num" style={{ color: '#4ade80' }}>{formatCurrency(totalWon)}</span>
          <span className="mid-kpi-label">Ganado este mes</span>
        </div>
        <div className="mid-kpi">
          <Zap size={20} style={{ color: '#60a5fa' }} />
          <span className="mid-kpi-num">{weekActivities}</span>
          <span className="mid-kpi-label">Completadas (7d)</span>
        </div>
      </div>

      {/* Alerts */}
      {(staleDeals.length > 0 || overdueActivities.length > 0) && (
        <div className="mid-alerts">
          {staleDeals.length > 0 && (
            <div className="mid-alert mid-alert-orange" onClick={() => navigate('/pipeline')}>
              <AlertTriangle size={16} />
              <span><strong>{staleDeals.length}</strong> deal{staleDeals.length > 1 ? 's' : ''} estancado{staleDeals.length > 1 ? 's' : ''} (+14 días)</span>
              <ChevronRight size={14} />
            </div>
          )}
          {overdueActivities.length > 0 && (
            <div className="mid-alert mid-alert-red" onClick={() => navigate('/calendar')}>
              <AlertTriangle size={16} />
              <span><strong>{overdueActivities.length}</strong> actividad{overdueActivities.length > 1 ? 'es' : ''} vencida{overdueActivities.length > 1 ? 's' : ''}</span>
              <ChevronRight size={14} />
            </div>
          )}
        </div>
      )}

      <div className="mid-grid">
        {/* Today's Schedule */}
        <div className="mid-card">
          <div className="mid-card-header">
            <h2><Calendar size={16} /> Agenda de Hoy</h2>
            <button className="mid-link" onClick={() => navigate('/calendar')}>Calendario <ChevronRight size={14} /></button>
          </div>
          {todayActivities.length === 0 ? (
            <div className="mid-empty-card">
              <Check size={24} style={{ color: '#4ade80' }} />
              <p>Sin actividades pendientes hoy</p>
            </div>
          ) : (
            <div className="mid-list">
              {todayActivities.map(a => (
                <div key={a.$id} className="mid-item">
                  <span className="mid-item-dot" style={{ background: a.type === 'instalacion' ? '#4ade80' : a.type === 'visita' ? '#60a5fa' : a.type === 'soporte' ? '#fb923c' : '#94a3b8' }} />
                  <div className="mid-item-content">
                    <span className="mid-item-title">{a.title}</span>
                    <span className="mid-item-meta">{a.type}{a.clientName ? ` · ${a.clientName}` : ''}{a.duration ? ` · ${a.duration}` : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Pipeline */}
        <div className="mid-card">
          <div className="mid-card-header">
            <h2><TrendingUp size={16} /> Mi Pipeline</h2>
            <button className="mid-link" onClick={() => navigate('/pipeline')}>Ver todo <ChevronRight size={14} /></button>
          </div>
          {activeDeals.length === 0 ? (
            <p className="mid-empty">Sin deals activos</p>
          ) : (
            <>
              <div className="mid-chart-sm">
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={dealsByStageChart} barSize={20}>
                    <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: '0.8rem' }}
                      formatter={(v) => [v, 'Deals']} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {dealsByStageChart.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mid-list">
                {activeDeals.slice(0, 4).map(d => {
                  const stageInfo = STAGES.find(s => s.value === d.stage) || STAGES[0]
                  const days = daysInStage(d)
                  const isStale = days > 14
                  return (
                    <div key={d.$id} className={`mid-item ${isStale ? 'mid-item-stale' : ''}`}>
                      <span className="mid-item-dot" style={{ background: stageInfo.color }} />
                      <div className="mid-item-content">
                        <span className="mid-item-title">{d.title}</span>
                        <span className="mid-item-meta">{stageInfo.label} · {formatCurrency(d.value)}{days != null ? ` · ${days}d` : ''}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* My Tickets */}
        <div className="mid-card">
          <div className="mid-card-header">
            <h2><Ticket size={16} /> Mis Tickets</h2>
            <button className="mid-link" onClick={() => navigate('/tickets')}>Ver todos <ChevronRight size={14} /></button>
          </div>
          {myTickets.length === 0 ? (
            <div className="mid-empty-card">
              <Check size={24} style={{ color: '#4ade80' }} />
              <p>Sin tickets asignados</p>
            </div>
          ) : (
            <div className="mid-list">
              {myTickets.slice(0, 5).map(t => (
                <div key={t.$id} className="mid-item">
                  <span className="mid-item-dot" style={{
                    background: t.priority === 'high' ? '#ef4444' : t.priority === 'medium' ? '#f59e0b' : '#10b981'
                  }} />
                  <div className="mid-item-content">
                    <span className="mid-item-title">{t.subject}</span>
                    <span className="mid-item-meta">{t.ticketNumber} · {t.clientName || '—'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Goals Snapshot */}
        <div className="mid-card">
          <div className="mid-card-header">
            <h2><Target size={16} /> Metas del Mes</h2>
            <button className="mid-link" onClick={() => navigate('/goals')}>Todas <ChevronRight size={14} /></button>
          </div>
          {myGoals.length === 0 ? (
            <p className="mid-empty">Sin metas definidas</p>
          ) : (
            <>
              <div className="mid-goals-summary">
                <span className="mid-goals-pct" style={{ color: Number(goalStats.avgProgress) >= 75 ? '#4ade80' : Number(goalStats.avgProgress) >= 50 ? '#f59e0b' : '#60a5fa' }}>
                  {goalStats.avgProgress}%
                </span>
                <span className="mid-goals-label">{goalStats.achieved}/{goalStats.total} logradas</span>
              </div>
              <div className="mid-goals-list">
                {myGoals.slice(0, 4).map(g => {
                  const progress = g.target > 0 ? Math.min(100, (g.current / g.target) * 100) : 0
                  const achieved = progress >= 100
                  return (
                    <div key={g.$id} className="mid-goal-item">
                      <span className="mid-goal-name">{g.metric}</span>
                      <div className="mid-goal-bar"><div style={{ width: `${progress}%`, background: achieved ? '#4ade80' : '#3b82f6' }} /></div>
                      <span className="mid-goal-pct">{progress.toFixed(0)}%</span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Recent Activity */}
        <div className="mid-card mid-card-full">
          <div className="mid-card-header">
            <h2><Activity size={16} /> Mi Actividad Reciente</h2>
            <button className="mid-link" onClick={() => navigate('/audit')}>Auditoría <ChevronRight size={14} /></button>
          </div>
          {myAudit.length === 0 ? (
            <p className="mid-empty">Sin actividad reciente</p>
          ) : (
            <div className="mid-timeline">
              {myAudit.map(l => (
                <div key={l.SK || l.$id} className="mid-timeline-item">
                  <div className="mid-timeline-dot" />
                  <div className="mid-timeline-content">
                    <span className="mid-timeline-action">{l.action}</span>
                    <span className="mid-timeline-desc">{l.description}</span>
                    <span className="mid-timeline-time">
                      {l.createdAt ? new Date(l.createdAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
